import { Injectable, inject } from '@angular/core'
import { catchError, map } from 'rxjs/operators'
import { Router } from '@angular/router'
import { Observable } from 'rxjs'

import { ApiService } from '../../modules/manage/sequential/services/api.service'
import { AuctionModel } from '../../modules/public/models/auction.model'
import { AuctionStatus } from '../../core/constants/auction-status.enum'
import { HeaderService } from '../../core/services/header.service'
import { BidderStatus } from '../../core/constants/bidder-status.enum'
import { ReserveStatus } from '../../core/constants/reserve-status.enum'
import { LotActivityStatus } from '../../core/constants'
import { LotService } from './lot.service'
import { LotHelper } from '../../modules/manage/saleyard/helpers/LotHelper'

@Injectable()
export class AuctionService {
    private apiService = inject(ApiService);
    private lotService = inject(LotService);
    private headerService = inject(HeaderService);
    private router = inject(Router);

    static messages = {
        startingSoon: 'Starting soon',
        started: 'In progress',
        paused: 'Paused',
        finished: 'Auction finished',
        startingSoonMsg: 'The auction is starting soon',
        finishedMsg: 'The auction is finished',
        pausedMsg: 'The auction is paused. Please wait for the auction to restart soon',
        notAvailable: 'The auction is not available'
    }

    timer
    bidtimer

    static initModel() {
        const result: AuctionModel = {
            canBid: false,
            isValidBid: true,
            showStatusMsg: false,
            timer: 0,
            nextLots: [],
            finishedLots: [],
            currentLot: <any>null,
            hasAccount: () => result.auction!.bidderAccount != null,
            approvedBidder: () =>
                result.auction != null &&
                result.hasAccount() &&
                result.auction.bidderAccount!.bidStatus === BidderStatus.Approved,
            userCanBid: () =>
                result.hasAccount() &&
                result.canBid &&
                result.auction!.bidderAccount !== undefined &&
                result.auction!.bidderAccount!.bidStatus === BidderStatus.Approved,
            showBackBtn: () => result.auction == null || result.auction.auctionStatus === AuctionStatus.Finished,
            hasCurrentLot: () => result.currentLot != null,
            isReserveMet: () => result.currentLot!.reserveStatus === ReserveStatus.ReserveMet,
            isNearReserve: () => result.currentLot!.reserveStatus === ReserveStatus.NearReserve,
            isReserveNotMet: () =>
                result.currentLot!.reserveStatus === ReserveStatus.None &&
                result.currentLot!.activityStatus === LotActivityStatus.NowSelling,
            isNowSelling: () => result.currentLot!.activityStatus === LotActivityStatus.NowSelling,
            isLotStarting: () => result.currentLot!.activityStatus === LotActivityStatus.Started,
            isFinishing: () => {
                const status = result.currentLot!.activityStatus
                return (
                    status === LotActivityStatus.Sold ||
                    status === LotActivityStatus.NotSold ||
                    status === LotActivityStatus.PassedIn ||
                    status === LotActivityStatus.FinishDelayed
                )
            },
            isOfferedToCurrentUser: () =>
                result.approvedBidder() &&
                LotHelper.isUnderOfferToBidderById(result.currentLot!, result.auction!.bidderAccount!.id)!,
            getTime: () => {
                if (!result.timer) {
                    return null
                }

                const seconds = Math.ceil(result.timer / 1000)
                const separator = ':'
                const date = new Date(0, 0, 0, 0, 0, seconds, 0)
                const time = date.toTimeString().split(' ')
                const timeArray = time[0].split(separator)
                return timeArray[1] + separator + timeArray[2]
            },
            primaryStreamUrl: null,
            secondaryStreamUrl: null,
            currentMediaIndex: 0,
            setCurrentMediaIndex: (index) => {
                result.currentMediaIndex = index
            }
        }

        return result
    }

    getAuction(id: number) {
        const result = AuctionService.initModel()
        return this.apiService.getAuction(id).pipe(
            map((r: any) => {
                // Setup auction
                this.setupAuction(result, r)
                // Setup media for upcoming lots
                r.lots.upComingLots.forEach((element) => {
                    this.lotService.setupMedia(element)
                })
                // Setup next lots
                result.nextLots = r.lots.upComingLots
                // Setup finished lots
                result.finishedLots = this.lotService.setupFinishedLots(result, r.lots.finishedLots)
                // Check whether there is a lot in progress
                if (r.inProgressLot != null) {
                    // Setup lot
                    this.lotService.setupLot(result, r)
                }

                return result
            }),
            catchError((e) => {
                if (e.status && e.status === 500) {
                    this.router.navigate(['error'])
                }

                return <Observable<AuctionModel>>{}
            })
        )
    }

    setupAuction(model: AuctionModel, r: any) {
        model.auction = r
        // Setup auction bidder
        model.auction!.bidderAccount = r.bidAccounts[0]
        model.auction!.saleTypeEnum = r.saleTypeEnum
        // Setup header
        model.title = '#' + r.id + ' ' + r.title
        // Set header
        this.headerService.setHeaderTitle(model.title)

        model.primaryStreamUrl = r.primaryStreamUrl
        model.secondaryStreamUrl = r.secondaryStreamUrl
        model.floorBidderId = r.floorBidderId
        model.floorBidderNumber = r.floorBidderNumber

        // Check wether auction is ready
        switch (r.auctionStatus) {
            case AuctionStatus.Ready: {
                this.setupOpenAuction(model)
                break
            }
            case AuctionStatus.InProgress: {
                this.setupStartedAuction(model)
                break
            }
            case AuctionStatus.Finished: {
                this.setupFinishedAuction(model)
                break
            }
            case AuctionStatus.Paused: {
                this.setupPausedAuction(model)
                break
            }
        }
    }

    setupOpenAuction(model: AuctionModel) {
        // Setup subtitle
        model.subtitle = AuctionService.messages.startingSoon
        // Apply subtitle change
        this.headerService.setHeaderSubtitle(model.subtitle)
        // Setup status message
        model.statusMsg = AuctionService.messages.startingSoonMsg
        // Set status message flag
        model.showStatusMsg = true
    }

    setupStartedAuction(model: AuctionModel) {
        // Set status message flag
        model.showStatusMsg = false
        // Set subtitle
        model.subtitle = AuctionService.messages.started
        // Change status of the Auction
        model.auction!.auctionStatus = AuctionStatus.InProgress
        // Apply changes to subtitle
        this.headerService.setHeaderSubtitle(model.subtitle)
    }

    setupPausedAuction(model: AuctionModel) {
        // update status message
        model.subtitle = AuctionService.messages.paused
        // Apply subtitle change
        this.headerService.setHeaderSubtitle(model.subtitle)
        // update title
        model.statusMsg = AuctionService.messages.pausedMsg
        // Set status message flag
        model.showStatusMsg = true
    }

    setupFinishedAuction(model: AuctionModel) {
        // Reset timer
        model.timer = 0
        // Set subtitle
        model.subtitle = AuctionService.messages.finished
        // Apply changes to sub title
        this.headerService.setHeaderSubtitle(model.subtitle)
        // Change status of the Auction
        model.auction!.auctionStatus = AuctionStatus.Finished
        // Setup status message
        model.statusMsg = AuctionService.messages.finishedMsg
        // Show status message
        model.showStatusMsg = true
        // Reset current lot
        model.finishedLots.push(model.currentLot!)
        model.currentLot = null
    }

    setAutoBid(model: AuctionModel) {
        return this.apiService.postAuctionAutoBids(
            model.auction!.id,
            model.currentLot!.id,
            model.currentLot!.autoBid,
            model.auction!.bidderAccount!.id
        )
    }

    removeAutoBid(model: AuctionModel) {
        return this.apiService.deleteAuctionAutoBids(model.auction!.id, model.currentLot!.id)
    }

    getAutoBid(model: AuctionModel) {
        return this.apiService.getAuctionAutoBid(model.auction!.id, model.currentLot!.id)
    }
}
